import { MEDICATION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import type { DosageData } from "@qendo/medication";
import type { ScheduleData } from "@qendo/schedule";
import { OptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import { router } from "expo-router";
import { useEffect, useId, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";

import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { DosageField } from "~/components/form/health-profile/DosageField";
import { YesNo } from "~/components/form/radio/YesNo";
import { ScheduleField } from "~/components/form/schedule/ScheduleField";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import type { Medication } from "~/entities/medical/medication";
import type { ApiError } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { filled } from "~/util";
import { elementsMatch } from "~/util/array";
import { ErrorBag } from "~/util/error-bag";
import { itemWidth, setWidthOnLayout } from "~/util/layout";
import { dosagesMatch, schedulesMatch } from "~/util/qendo/health-profile";

type MedicationFormProps = {
    readonly medication?: Medication;
    onRequestClose: () => void;
} & FullPageModalProps;

export const MedicationForm = ({ medication, onRequestClose, ...modalProps }: MedicationFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving, setError } = useFeedback();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const [option, setOption] = useState<string | null>(medication?.option_id ?? null);
    const [prescribed, setPrescribed] = useState(medication?.prescribed ?? false);
    const [current, setCurrent] = useState<boolean | null>(medication?.current ?? null);
    const [description, setDescription] = useState(medication?.description ?? "");
    const [startDate, setStartDate] = useState<DateArg<Date> | null>(medication?.start_date ?? null);
    const [endDate, setEndDate] = useState<DateArg<Date> | null>(medication?.end_date ?? null);
    const [photographs, setPhotographs] = useState<string[]>(medication?.photograph_ids ?? []);
    const [dosage, setDosage] = useState<DosageData | null>(medication?.dosage ?? null);
    const [schedule, setSchedule] = useState<ScheduleData | null>(medication?.schedule ?? null);
    const date_width = useMemo(() => Math.floor(itemWidth(width, 2)), [width]);
    const errors = useMemo<ErrorBag<"option_id" | "start_date" | "end_date">>(() => new ErrorBag(), []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const canSave = canUpdateMedical(MedicalType.medication) && !saving && option !== null;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (medication?.option_id ?? null) ||
                prescribed !== (medication?.prescribed ?? false) ||
                current !== (medication?.current ?? null) ||
                description !== (medication?.description ?? "") ||
                startDate !== (medication?.start_date ?? null) ||
                endDate !== (medication?.end_date ?? null) ||
                !dosagesMatch(dosage, medication?.dosage) ||
                !schedulesMatch(schedule, medication?.schedule) ||
                !elementsMatch(photographs, medication?.photograph_ids ?? []),
        );
    }, [medication, option, description, prescribed, current, photographs, startDate, endDate, dosage, schedule]);

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.medication,
            id: medication?.id,
            option_id: option,
            photograph_ids: photographs,
            prescribed,
            current: current ?? false,
            description,
            start_date: startDate,
            end_date: endDate,
            dosage,
            schedule,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${MEDICATION_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .catch((error: ApiError<"option_id" | "start_date" | "end_date">) => {
                if (error.status === 422 && error.response?.data?.errors) {
                    errors.fill(error.response.data.errors);
                } else {
                    setError(error.response?.data?.message ?? error.message);
                }
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${medication?.id ? "Edit" : "Add"} medication`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.medications}
                    value={option}
                    onItemSelected={setOption}
                    label="Medication"
                    disabled={is_saving}
                    name="option_id"
                />
                <DosageField value={dosage} onUpdateDosage={setDosage} disabled={is_saving} />
                <CheckboxInput
                    value={prescribed}
                    onValueChange={setPrescribed}
                    label="Prescribed medication"
                    disabled={is_saving}
                    icon="prescribed"
                />
            </FieldGroup>
            <FieldGroup>
                <YesNo title="Do you currently take this?" value={current} onValueChange={setCurrent} />
                {filled(current) && (
                    <ScheduleField
                        value={schedule}
                        onUpdateSchedule={setSchedule}
                        disabled={is_saving}
                        dosage={dosage}
                    />
                )}
                {current === false && (
                    <View style={styles.dates} onLayout={onLayout}>
                        <DatePicker
                            label="Started"
                            value={startDate}
                            onDateSelected={setStartDate}
                            max={endDate}
                            optional
                            style={{ width: date_width }}
                            name="start_date"
                            clearable
                        />
                        <DatePicker
                            label="Ended"
                            value={endDate}
                            onDateSelected={setEndDate}
                            min={startDate}
                            optional
                            style={{ width: date_width }}
                            name="end_date"
                            clearable
                        />
                    </View>
                )}
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
const styles = StyleSheet.create({
    dates: {
        flexDirection: "row",
        gap: 12,
        alignItems: "center",
    },
});
